import React from 'react';
import styles from './styles.module.scss';
import { TickIcon } from 'assets';

interface AssessmentInformationProps {
  companyName?: string;
  assessmentTitle: string;
  startDate: string;
  endDate: string;
  totalQuestions: number;
  instructions?: string[];
  reminderNote?: string;
  totalTime?: string;
  /**
   * The metrics actually included in this assessment. When provided, only these
   * areas are shown to the candidate. Falls back to the full set when omitted
   * (e.g. the questions are still loading) so the list is never empty.
   */
  areasCovered?: string[];
}

// Full set of areas an assessment can cover. Used as a fallback only when the
// included metrics haven't been resolved yet.
const DEFAULT_AREAS_COVERED = ["Professional Expertise", "Interpersonal Effectiveness", "Work Drivers", "Work Environment Preferences", "Execution Approach", "Personality"];

const AssessmentInformation: React.FC<AssessmentInformationProps> = ({
  companyName,
  assessmentTitle,
  startDate,
  endDate,
  totalQuestions,
  instructions,
  reminderNote,
  totalTime,
  areasCovered,
}) => {
  const displayInstructions = instructions || [];
  const displayTotalTime = totalTime || '60 minutes';
  const appreciationNote = "We appreciate your interest in this position and would like to assess your abilities in alignment with our requirements. This assessment is designed to evaluate your skills, knowledge, and suitability for a specific role within our organization.";
  // Fall back only while the areas are unresolved. An empty list is a real
  // answer (the recruiter removed every metric) and must not resurrect the
  // default list, which would advertise areas the candidate won't be tested on.
  const displayAreasCovered = areasCovered ?? DEFAULT_AREAS_COVERED;
  return (
    <div className={styles.assessmentContainer}>
      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <p>Your assessment with {companyName || 'the employer'} is about to start, but here is some information to help you.</p>
        </div>
        <div className={styles.divider}></div>
      </div>

      <div className={styles.assessmentDetailsContainer}>
        <div className={styles.titleContainer}>
          <h3 className={styles.assessmentTitle}>{assessmentTitle}</h3>
        </div>

        <div className={styles.detailsGrid}>
          <div className={styles.detailsRow}>
            <span className={styles.detailLabel}>Start Date:</span>
            <span className={styles.detailValue}>{startDate}</span>
          </div>

          <div className={styles.detailsRow}>
            <span className={styles.detailLabel}>End Date:</span>
            <span className={styles.detailValue}>{endDate}</span>
          </div>

          <div className={styles.detailsRow}>
            <span className={styles.detailLabel}>Total Time:</span>
            <span className={styles.detailValue}>{displayTotalTime}</span>
          </div>

          <div className={styles.detailsRow}>
            <span className={styles.detailLabel}>Total Questions:</span>
            <span className={styles.detailValue}>{totalQuestions}</span>
          </div>
        </div>


        <div className={styles.divider}></div>
      </div>

      <div className={styles.sectionContainer}>
        <p className={styles.appreciationText}>{appreciationNote}</p>
        <span className={styles.subDetailValue}>Please read the instructions carefully before proceeding.</span>
        <div className={styles.divider}></div>
      </div>

      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Instructions</h3>
        </div>
        <ol className={styles.listContainer}>
          {displayInstructions.map((instruction, index) => (
            <li key={index} className={styles.listItem}>{instruction}</li>
          ))}
        </ol>
        {reminderNote && <p className={styles.rememberText}>{reminderNote}</p>}
        <div className={styles.divider}></div>
      </div>

      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Questions in this assessment cover the following areas</h3>
        </div>

        {displayAreasCovered.length > 0 ? (
          <ul className={styles.listContainerSkills}>
            {displayAreasCovered.map((area, index) => (
              <li key={index} className={`flex items-center gap-2 ${styles.listItemSkills}`}>
                <span className={styles.skillsIcon}><TickIcon /></span> {area}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyState}>No areas covered provided.</p>
        )}
        <div className={styles.divider}></div>

        <p>Would you like a quick guide before you begin your assessment?</p>
      </div>
    </div>
  );
};

export { AssessmentInformation };